export var AggregateFunction;
(function (AggregateFunction) {
    AggregateFunction[AggregateFunction["None"] = 0] = "None";
    AggregateFunction[AggregateFunction["Sum"] = 1] = "Sum";
    AggregateFunction[AggregateFunction["Average"] = 2] = "Average";
    AggregateFunction[AggregateFunction["Count"] = 3] = "Count";
    AggregateFunction[AggregateFunction["Maximum"] = 4] = "Maximum";
    AggregateFunction[AggregateFunction["Minimum"] = 5] = "Minimum";
    AggregateFunction[AggregateFunction["Variance"] = 6] = "Variance";
})(AggregateFunction || (AggregateFunction = {}));
export var DateModifier;
(function (DateModifier) {
    DateModifier[DateModifier["None"] = 0] = "None";
    DateModifier[DateModifier["Year"] = 1] = "Year";
    DateModifier[DateModifier["Quarter"] = 2] = "Quarter";
    DateModifier[DateModifier["Month"] = 3] = "Month";
    DateModifier[DateModifier["DayOfYear"] = 4] = "DayOfYear";
    DateModifier[DateModifier["DayOfMonth"] = 5] = "DayOfMonth";
    DateModifier[DateModifier["Week"] = 6] = "Week";
    DateModifier[DateModifier["Weekday"] = 7] = "Weekday";
    DateModifier[DateModifier["Hour"] = 8] = "Hour";
    DateModifier[DateModifier["Minute"] = 9] = "Minute";
    DateModifier[DateModifier["Second"] = 10] = "Second";
    DateModifier[DateModifier["Millisecond"] = 11] = "Millisecond";
})(DateModifier || (DateModifier = {}));
export class SelectCol {
    aggregateFunction;
    dateModifier;
    name;
    script;
    groupBy;
    fullName;
    title; //for using in ui
    value;
    nameScript;
    valScript;
    selectAsTitle;
    /** Nested query supplying the value set; pair with `QueryFunction.MatchArrays`. */
    valQuery;
    /** Deduplicate on this column. Also accepted at the root of the query. */
    distinct;
    dataType;
    /** Compute or join the column but omit it from the response. */
    hideFromResults;
    /** Entity definition supplied inline instead of by name. */
    dynamicDefinition;
    skipProcess;
    /** Search text to highlight inside the returned column value. */
    searchHighlight;
    // --- Presentation. Persisted by GSB in GsbUserQuery.query; see docs/plans. ---
    /** Identity of the column config itself, not of the property. */
    id;
    /** 1-based position within the view. */
    orderNumber;
    /** Pixel width. GSB stores `""` for "size automatically". */
    width;
    /** ScreenType bitmask: 7 = all, 3 = PC|Tablet, 0 = hidden everywhere. */
    visibleScreens;
    /** Selected by the view but not rendered. */
    hidden;
    visibility;
    /** Desktop styling. `styleSM` is tablet, `styleXS` is mobile. */
    style;
    styleSM;
    styleXS;
    /** Flat predecessors of `style`, still present in views authored before it existed. */
    headerStyle;
    cellStyle;
    categoryGroup_id;
    /** Emitted by `transposeScript`; the column has no backing property. */
    clientOnly;
    autoGenerated;
    constructor(cName = undefined) {
        this.name = cName;
    }
}
export class SortCol {
    col;
    sortType;
}
/**
 * Comparison operator for a filter. The server accepts the enum number or the
 * member name as a case-insensitive string (e.g. 12 or "Contains" or "contains").
 *
 * Reference fields: `In`, `Equals`, and `Contains` all match a given id-array
 * against the related ids. On a multi-reference property, `Contains` targets the
 * property name directly (`roles`); `In`/`Equals` need the dotted id path
 * (`roles.id`) — `In` on the bare property name fails server-side.
 */
export var QueryFunction;
(function (QueryFunction) {
    /** Exact value match. */
    QueryFunction[QueryFunction["Equals"] = 0] = "Equals";
    /** Substring/pattern match with % wildcards — the operator for text search. */
    QueryFunction[QueryFunction["Like"] = 1] = "Like";
    QueryFunction[QueryFunction["Greater"] = 2] = "Greater";
    QueryFunction[QueryFunction["Smaller"] = 3] = "Smaller";
    QueryFunction[QueryFunction["NotEqual"] = 4] = "NotEqual";
    QueryFunction[QueryFunction["BitwiseAnd"] = 5] = "BitwiseAnd";
    QueryFunction[QueryFunction["BitwiseOr"] = 6] = "BitwiseOr";
    QueryFunction[QueryFunction["BitwiseXor"] = 7] = "BitwiseXor";
    /** Value is in the given set. On a reference, matches related ids via `ref.id`. */
    QueryFunction[QueryFunction["In"] = 8] = "In";
    QueryFunction[QueryFunction["Is"] = 9] = "Is";
    QueryFunction[QueryFunction["IsNot"] = 10] = "IsNot";
    QueryFunction[QueryFunction["FullTextSearch"] = 11] = "FullTextSearch";
    /** Multi-reference/array membership — NOT substring. Use Like for text. */
    QueryFunction[QueryFunction["Contains"] = 12] = "Contains";
    QueryFunction[QueryFunction["GreaterOrEqual"] = 13] = "GreaterOrEqual";
    QueryFunction[QueryFunction["SmallerOrEqual"] = 14] = "SmallerOrEqual";
    /** Case-insensitive Like. */
    QueryFunction[QueryFunction["ILike"] = 15] = "ILike";
    QueryFunction[QueryFunction["RegexMatch"] = 16] = "RegexMatch";
    QueryFunction[QueryFunction["RegexMatchCaseInsensitive"] = 17] = "RegexMatchCaseInsensitive";
    QueryFunction[QueryFunction["IsNull"] = 18] = "IsNull";
    /** Range test. Bounds go in `extra: { value1, value2 }` on the filter, not in `val`. */
    QueryFunction[QueryFunction["Between"] = 19] = "Between";
    /** BROKEN server-side (bad phraseto_tsquery call, HTTP 500); use FullTextSearch or Like. */
    QueryFunction[QueryFunction["PhraseSearch"] = 20] = "PhraseSearch";
    QueryFunction[QueryFunction["GeometryOverlaps"] = 21] = "GeometryOverlaps";
    QueryFunction[QueryFunction["PointInGeometry"] = 22] = "PointInGeometry";
    QueryFunction[QueryFunction["GPSDistance"] = 23] = "GPSDistance";
    QueryFunction[QueryFunction["GPSWithinRadius"] = 24] = "GPSWithinRadius";
    QueryFunction[QueryFunction["JsonContains"] = 25] = "JsonContains";
    QueryFunction[QueryFunction["JsonHasKey"] = 26] = "JsonHasKey";
    /** Advanced: matches a column against the value-set returned by a subquery set on `col`/`val`. For plain array/id membership use `Contains`. */
    QueryFunction[QueryFunction["MatchArrays"] = 27] = "MatchArrays";
})(QueryFunction || (QueryFunction = {}));
export var QueryRelation;
(function (QueryRelation) {
    QueryRelation["And"] = "and";
    QueryRelation["Or"] = "or";
})(QueryRelation || (QueryRelation = {}));
export var QuerySortType;
(function (QuerySortType) {
    QuerySortType["Ascending"] = "asc";
    QuerySortType["Descending"] = "desc";
})(QuerySortType || (QuerySortType = {}));
export class PropertyValue {
    name;
    value;
    valueScript;
    type;
    label;
    constructor(name = undefined, value = undefined, label = undefined, type = undefined) {
        this.name = name;
        this.value = value;
        this.label = label;
        this.type = type;
    }
}
export class Filter {
    col;
    val;
    relationLevel;
    children;
    /** How this filter joins to the *preceding* filter. Defaults to `and`. */
    relation;
    name;
    /** Inverts a single filter. Ignored on a `children` group. */
    negate = false;
    /** Extra operands for multi-value functions, e.g. `Between`. */
    extra;
    function;
    get queryFunction() {
        return this.function;
    }
    constructor(propName = undefined, propVal = undefined, func = undefined, relation = undefined) {
        this.col = new SelectCol(propName);
        this.val = new SelectCol();
        this.val.value = propVal;
        this.function = func;
        this.relation = relation;
    }
    aggregate(func) {
        if (!this.col)
            this.col = new SelectCol();
        this.col.aggregateFunction = func;
        return this;
    }
    groupBy(value = true) {
        if (!this.col)
            this.col = new SelectCol();
        this.col.groupBy = value;
        return this;
    }
    isEqual(value) {
        if (!this.val)
            this.val = new SelectCol();
        this.function = QueryFunction.Equals;
        this.val.value = value;
        return this;
    }
    isLike(value) {
        if (!this.val)
            this.val = new SelectCol();
        this.function = QueryFunction.Like;
        this.val.value = value;
        return this;
    }
    isGreater(value) {
        if (!this.val)
            this.val = new SelectCol();
        this.function = QueryFunction.Greater;
        this.val.value = value;
        return this;
    }
    isSmaller(value) {
        if (!this.val)
            this.val = new SelectCol();
        this.function = QueryFunction.Smaller;
        this.val.value = value;
        return this;
    }
    /** Array/multi-reference membership: matches rows whose related-id set contains the given id(s). Not a substring operator. */
    contains(value) {
        if (!this.val)
            this.val = new SelectCol();
        this.function = QueryFunction.Contains;
        this.val.value = value;
        return this;
    }
    bitwiseAnd(value) {
        if (!this.val)
            this.val = new SelectCol();
        this.function = QueryFunction.BitwiseAnd;
        this.val.value = value;
        return this;
    }
    bitwiseOr(value) {
        if (!this.val)
            this.val = new SelectCol();
        this.function = QueryFunction.BitwiseOr;
        this.val.value = value;
        return this;
    }
    bitwiseXor(value) {
        if (!this.val)
            this.val = new SelectCol();
        this.function = QueryFunction.BitwiseXor;
        this.val.value = value;
        return this;
    }
    in(value) {
        if (!this.val)
            this.val = new SelectCol();
        this.function = QueryFunction.In;
        this.val.value = value;
        return this;
    }
    fullTextSeach(value) {
        if (!this.val)
            this.val = new SelectCol();
        this.function = QueryFunction.FullTextSearch;
        this.val.value = value;
        return this;
    }
    is(value) {
        if (!this.val)
            this.val = new SelectCol();
        this.function = QueryFunction.Is;
        this.val.value = value;
        return this;
    }
    not() {
        this.negate = true;
        return this;
    }
    funcVal(queryFunction, value) {
        if (!this.val)
            this.val = new SelectCol();
        this.function = queryFunction;
        this.val.value = value;
        return this;
    }
}
//# sourceMappingURL=query.js.map